import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';
import { SUPPORT_EMAIL } from '../utils/constants';

const Privacy = () => {
  return (
    <LegalPage title="Privacy Policy" updated="June 12, 2026">
      <p>
        Solidly makes Solidly Meet, a desktop app sold on this website (meet.solidly.ai). The
        product is local-first by design: your meeting transcripts, notes, voice recognition data,
        and skills live in a local database on your machine, not on our servers. There is no cloud
        account. This page explains, in plain English, what data we do touch — on this website
        and in the app — and what we never see.
      </p>

      <h2>What this website collects</h2>
      <ul>
        <li>
          <strong>Analytics.</strong> We use Google Analytics 4 to understand how visitors use
          this site (pages viewed, buttons clicked, rough location and device type). Google sets
          cookies to do this. We don't use the data for advertising, and we don't sell it.
        </li>
        <li>
          <strong>Payments.</strong> Checkout is handled entirely by Stripe. Your card details
          go to Stripe, not to Solidly — we never see or store your payment information. After a
          purchase, we receive your email address and a record of the purchase so we can send
          your license key and honor refunds.
        </li>
        <li>
          <strong>The email form.</strong> If you ask us to send you the demo, we store the
          email address you give us, and use it only for the handful of emails the form
          describes. Unsubscribe anytime.
        </li>
      </ul>

      <h2>What we store to run your license</h2>
      <ul>
        <li>
          When you buy, we store your email address and license key in our database (AWS
          DynamoDB), and send your license key to you by email (via AWS SES).
        </li>
        <li>
          When you activate the app, we store an identifier for that device alongside your
          license, so we can enforce the 3-device activation limit. That's the extent of what
          license activation sends us.
        </li>
      </ul>

      <h2>What the app does with your data</h2>
      <ul>
        <li>
          <strong>Your content stays on your machine.</strong> Transcripts, meeting notes,
          summaries, voice recognition data, skills, and settings are stored in a local SQLite database on
          your device. We run no servers that receive your meeting content, and we have no
          access to it.
        </li>
        <li>
          <strong>Audio goes to AssemblyAI — under your key.</strong> During a meeting, captured
          audio streams from your machine directly to AssemblyAI for live transcription, using
          the API key you provide, under AssemblyAI's terms. It does not pass through Solidly.
        </li>
        <li>
          <strong>Prompts go to Claude — under your subscription.</strong> Live support cards,
          mid-meeting questions, summaries, and next-step drafts are generated by Claude through
          your own Claude Code subscription. Transcript excerpts and your notes are sent from
          your machine directly to Anthropic under your account and their terms — not through
          Solidly.
        </li>
        <li>
          <strong>Notes delivery is your MCP servers.</strong> If you deliver notes to tools
          like Notion, Linear, Slack, Obsidian, or Google Drive, that happens through MCP
          servers you configure, using your own accounts and credentials. We're not in that
          path either.
        </li>
      </ul>

      <h2>What we don't do</h2>
      <ul>
        <li>We don't sell or share your data with third parties for marketing.</li>
        <li>We don't see, store, or read your meetings, transcripts, notes, or voice recognition data.</li>
        <li>We don't resell or proxy your Claude or AssemblyAI usage.</li>
        <li>We don't require — or even offer — a cloud account or subscription.</li>
      </ul>

      <h2>Refunds</h2>
      <p>
        Every purchase comes with a 7-day money-back guarantee — email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> within 7 days for a full refund.
        See the <Link to="/terms">Terms</Link> for details.
      </p>

      <h2>Questions</h2>
      <p>
        Anything unclear, or want your data deleted? Email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and a human will answer.
      </p>
    </LegalPage>
  );
};

export default Privacy;
